import { useMemo, useState } from 'react';
import { ArrowLeftRight } from 'lucide-react';
import { convertSize } from '../../utils/convertSize';
import SystemSelector from './SystemSelector';
import SizeInput from './SizeInput';
import { getSizeOptions, getSystemsForCategory } from '../../utils/convertSize';

function MiniResult({ result, t, categoryId, label }) {
  if (!result) {
    return (
      <div className="rounded-xl border border-dashed border-slate-600 p-6 text-center text-sm text-slate-500">
        {label}
      </div>
    );
  }
  const { row } = result;
  const fields =
    categoryId === 'shoes' || categoryId === 'kids-shoes'
      ? ['eu', 'uk', 'us', 'cm']
      : categoryId === 'jeans'
        ? ['waistIn', 'international', 'eu']
        : ['international', 'eu', 'uk', 'us'];

  return (
    <div className="rounded-xl border border-slate-700 bg-slate-800/60 p-4">
      <p className="text-xs text-emerald-400 font-semibold mb-3">{label}</p>
      <dl className="space-y-2 text-sm">
        {fields.map((key) =>
          row[key] ? (
            <div key={key} className="flex justify-between gap-2">
              <dt className="text-slate-500">{t.results[key] || key}</dt>
              <dd className="text-white font-medium">{row[key]}</dd>
            </div>
          ) : null
        )}
      </dl>
    </div>
  );
}

export default function CompareSizes({ t, categoryId, gender, systemId, onSystemChange }) {
  const [sizeA, setSizeA] = useState('');
  const [sizeB, setSizeB] = useState('');

  const systems = useMemo(() => getSystemsForCategory(categoryId), [categoryId]);
  const options = useMemo(
    () => getSizeOptions(categoryId, gender, systemId),
    [categoryId, gender, systemId]
  );

  const resultA = useMemo(
    () =>
      sizeA
        ? convertSize({ categoryId, gender, systemId, sizeValue: sizeA })
        : null,
    [sizeA, categoryId, gender, systemId]
  );
  const resultB = useMemo(
    () =>
      sizeB
        ? convertSize({ categoryId, gender, systemId, sizeValue: sizeB })
        : null,
    [sizeB, categoryId, gender, systemId]
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm text-slate-400">
        <ArrowLeftRight className="w-4 h-4 text-emerald-500" />
        {t.compare.hint}
      </div>
      <SystemSelector
        label={t.converter.system}
        systems={systems}
        value={systemId}
        onChange={onSystemChange}
        systemLabels={t.systems}
      />
      <div className="grid sm:grid-cols-2 gap-4">
        <SizeInput
          label={t.compare.sizeA}
          placeholder={t.converter.sizePlaceholder}
          options={options}
          value={sizeA}
          onChange={setSizeA}
        />
        <SizeInput
          label={t.compare.sizeB}
          placeholder={t.converter.sizePlaceholder}
          options={options}
          value={sizeB}
          onChange={setSizeB}
        />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <MiniResult result={resultA} t={t} categoryId={categoryId} label={t.compare.resultA} />
        <MiniResult result={resultB} t={t} categoryId={categoryId} label={t.compare.resultB} />
      </div>
    </div>
  );
}
